import { MutableRefObject } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { merge } from 'lodash';
import {
  AllPlanScheduleType,
  MealDetailsByDate,
  RoutineActivity,
  ScheduledAction,
  StreamChatSessionType,
  TaskItem,
} from '@demind-inc/core';
import { SetterOrUpdater } from 'recoil';

import { ScheduleEvent, RoutineAdjustment, TodoAdjustment } from '../../../api/science/openapi';
import { AssistantChatFeedbackData, SuggestAllAIPlanState } from '../../../recoil';
import {
  AssistantChatMessage,
  ChatSuggestedEvent,
  InsightSuggestion,
  UnifiedChatRichContext,
  UnifiedChatRichContextEntry,
} from '../../../types';
import { ChatState } from '../types';
import { ChatResultJson, ChatUnifiedResponseEntry } from '../../../helpers/resultJson.schema';
import { transformAllPlanV7ScheduleEventsToScheduleAction } from '../../../../helpers/scheduler.helpers';
import { transformResultJsonSchedulesToSuggestedEvents } from '../../../helpers/resultJsonSchedule.helpers';
import {
  getRemovedMealEventIds,
  transformResultJsonMealsToSuggestedEvents,
  transformResultJsonSleepToSuggestedEvent,
} from '../../../helpers/resultJsonMealSleepAdjustments.helpers';
import {
  getRemovedTaskEventIds,
  transformResultJsonTasksToSuggestedEvents,
} from '../../../helpers/resultJsonTaskAdjustments.helpers';
import { transformResultJsonRoutinesToSuggestedEvents } from '../../../helpers/resultJsonRoutineAdjustments.helpers';
/** Opaque sync-chat result — concrete shape lives in useFormattedResultHandler. */
export type FormattedResult = Record<string, unknown>;


type MessageIdMap = MutableRefObject<Map<string, Set<string>>>;
type BlocksByMessageId = MutableRefObject<Map<string, ChatSuggestedEvent[]>>;

export interface PlanResultRuntime {
  isV4Enabled: boolean;
  user?: { userId: string; metricId?: string; routineId?: string } | null;
  calendarId?: string;
  addMessage: (message: AssistantChatMessage) => void;
  updateMessage: (messageId: string, patch: Partial<AssistantChatMessage>) => void;
  updateState: (updates: Partial<ChatState>) => void;
  setTemporalSuggestedEvents: (
    events: ChatSuggestedEvent[] | ((prevEvents: ChatSuggestedEvent[]) => ChatSuggestedEvent[])
  ) => void;
  setSuggestAllAIPlanState: SetterOrUpdater<SuggestAllAIPlanState>;
  setFeedbackData: SetterOrUpdater<AssistantChatFeedbackData | null>;
  getMealOnDate: (date: Dayjs) => MealDetailsByDate | undefined;
  findCurrentTasks: () => TaskItem[];
  findCurrentRoutineActivities: () => RoutineActivity[];
  syncChatResult: (params: {
    sessionId: string | undefined;
    sessionType: StreamChatSessionType;
    resultJson: string;
    userId: string;
    metricId: string;
    calendarId?: string;
    dateToBeSuggested?: string;
    routineId?: string;
  }) => Promise<FormattedResult | any>;
  handleFormattedResult: (
    output: FormattedResult,
    sessionType: StreamChatSessionType,
    date?: string,
    adjustmentEntries?: UnifiedChatRichContextEntry[],
    queryRichContexts?: UnifiedChatRichContextEntry[]
  ) => void;
  discardPendingAllPlanSuggestions: (
    typesToDiscard: AllPlanScheduleType[],
    targetDate?: string
  ) => Promise<void>;
  streamingDataRef: { readonly current: { streamingMessageId?: string } };
  latestResultJsonRef: MutableRefObject<ChatResultJson | null>;
  latestSuggestionsRef: MutableRefObject<InsightSuggestion[]>;
  inlineScheduleRef: MutableRefObject<{ messageId: string; date: string } | undefined>;
  scheduleMessageSeqRef: MutableRefObject<number>;
  scheduleMessageIdsByActionIdRef: MessageIdMap;
  scheduleBlocksByMessageIdRef: BlocksByMessageId;
  sleepMessageIdsByEventIdRef: MessageIdMap;
  sleepBlocksByMessageIdRef: BlocksByMessageId;
  mealMessageIdsByEventIdRef: MessageIdMap;
  mealBlocksByMessageIdRef: BlocksByMessageId;
  taskMessageIdsByEventIdRef: MessageIdMap;
  taskBlocksByMessageIdRef: BlocksByMessageId;
  routineMessageIdsByEventIdRef: MessageIdMap;
  routineBlocksByMessageIdRef: BlocksByMessageId;
}

const ARRAY_ADJUSTMENT_TYPES = ['meals', 'schedule', 'todo_list', 'routine_adjustments'] as const;

export const getAllPlanTypesToDiscardForChatOutput = (
  sessionType: StreamChatSessionType | string,
  prompt?: string
): AllPlanScheduleType[] => {
  // Legacy full plan-my-day session only. `scheduler_chat` follow-ups merge
  // partial schedule adjustments into suggestAllAIPlanState and must keep
  // prior pending plan-my-day items (matches lifestack_calendar).
  if (sessionType === 'scheduler') {
    return ['todo', 'routine'];
  }
  if (sessionType === 'task') {
    return ['todo'];
  }
  const normalizedPrompt = (prompt ?? '').toLowerCase();
  if (normalizedPrompt.includes('plan my workout')) {
    return ['workout'];
  }
  return [];
};

const readTrimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const readPresentString = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() ? value : fallback;

const readCtaAction = (raw: Record<string, unknown>): InsightSuggestion['cta_action'] => {
  const cta = raw.cta_action;
  if (!cta || typeof cta !== 'object') {
    return undefined;
  }
  const record = cta as Record<string, unknown>;
  const payload = record.payload;
  return {
    action_type: typeof record.action_type === 'string' ? record.action_type : 'none',
    payload:
      payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : undefined,
  };
};

const toInsightSuggestion = (item: unknown, index: number): InsightSuggestion | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const raw = item as Record<string, unknown>;
  const title = readTrimmed(raw.title);
  const description = readTrimmed(raw.description);
  if (!title || !description) {
    return null;
  }
  return {
    id: readPresentString(raw.id, `insight_${Date.now()}_${index}`),
    title,
    description,
    signal_source: readPresentString(raw.signal_source, 'combined'),
    urgency: readPresentString(raw.urgency, 'soon'),
    expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : undefined,
    cta_action: readCtaAction(raw),
  };
};

export const normalizeInsightSuggestions = (rawSuggestions: unknown): InsightSuggestion[] => {
  if (!Array.isArray(rawSuggestions)) {
    return [];
  }
  return rawSuggestions
    .map((item, index) => toInsightSuggestion(item, index))
    .filter((item): item is InsightSuggestion => item !== null)
    .slice(0, 3);
};

const readRichContext = (item: unknown): UnifiedChatRichContext | undefined => {
  if (!item || typeof item !== 'object') {
    return undefined;
  }
  const richContext = (item as { rich_context?: unknown }).rich_context;
  if (!richContext || typeof richContext !== 'object') {
    return undefined;
  }
  return richContext as UnifiedChatRichContext;
};

const pushItemRichContexts = (
  entries: UnifiedChatRichContextEntry[],
  items: unknown,
  adjType: string,
  date: string | undefined,
  baseSuggestionId: string
) => {
  if (!Array.isArray(items)) {
    return;
  }
  items.forEach((item, itemIdx) => {
    const richContext = readRichContext(item);
    if (!richContext) {
      return;
    }
    entries.push({
      date,
      suggestionId: `${baseSuggestionId}_${adjType}_${itemIdx}`,
      richContext,
      source: 'adjustment',
    });
  });
};

const pushAdjustmentRichContexts = (
  entries: UnifiedChatRichContextEntry[],
  adjustments: ChatUnifiedResponseEntry['adjustments'],
  date: string | undefined,
  baseSuggestionId: string
) => {
  if (!adjustments || typeof adjustments !== 'object') {
    return;
  }
  ARRAY_ADJUSTMENT_TYPES.forEach((adjType) => {
    pushItemRichContexts(entries, adjustments[adjType], adjType, date, baseSuggestionId);
  });
  const sleepContext = readRichContext(adjustments.sleep);
  if (!sleepContext) {
    return;
  }
  entries.push({
    date,
    suggestionId: `${baseSuggestionId}_sleep`,
    richContext: sleepContext,
    source: 'adjustment',
  });
};

const responseSuggestionId = (
  entry: ChatUnifiedResponseEntry | undefined,
  responseIdx: number
): string => {
  if (typeof entry?.suggestion_id === 'string' && entry.suggestion_id.trim()) {
    return entry.suggestion_id;
  }
  return entry?.date || `response_${responseIdx}`;
};

export const extractRichContextEntries = (
  resultJson: ChatResultJson
): UnifiedChatRichContextEntry[] => {
  const entries: UnifiedChatRichContextEntry[] = [];
  const responses = Array.isArray(resultJson.unified_chat_responses)
    ? resultJson.unified_chat_responses
    : [];

  responses.forEach((responseEntry, responseIdx) => {
    const date = typeof responseEntry?.date === 'string' ? responseEntry.date : undefined;
    const baseSuggestionId = responseSuggestionId(responseEntry, responseIdx);
    pushAdjustmentRichContexts(entries, responseEntry?.adjustments, date, baseSuggestionId);

    const queryContext = readRichContext(responseEntry?.query);
    if (!queryContext) {
      return;
    }
    entries.push({
      date,
      suggestionId: baseSuggestionId,
      richContext: queryContext,
      source: 'query',
    });
  });

  const rootQueryContext = readRichContext(resultJson.query);
  if (rootQueryContext) {
    entries.push({ suggestionId: 'flat', richContext: rootQueryContext, source: 'query' });
  }
  return entries;
};

export const readScheduleItems = (resultJson: ChatResultJson): ScheduleEvent[] => {
  if (Array.isArray(resultJson.schedules)) {
    return resultJson.schedules;
  }
  if (!Array.isArray(resultJson.unified_chat_responses)) {
    return [];
  }
  return resultJson.unified_chat_responses.flatMap((entry) => entry?.adjustments?.schedule ?? []);
};

const nextResultMessageId = (runtime: PlanResultRuntime, prefix: string): string =>
  `${prefix}_${Date.now()}_${runtime.scheduleMessageSeqRef.current++}`;

const syncSuggestionCard = (
  runtime: PlanResultRuntime,
  blocks: ChatSuggestedEvent[],
  messageIdsByEventIdRef: MessageIdMap,
  blocksByMessageIdRef: BlocksByMessageId,
  messageIdPrefix: string,
  removedEventIds: Set<string> = new Set()
): void => {
  if (blocks.length === 0) {
    return;
  }

  const updatedById = new Map(blocks.map((block) => [block.eventId, block]));
  const messageIdsToSync = new Set<string>();
  blocks.forEach((block) => {
    messageIdsByEventIdRef.current.get(block.eventId)?.forEach((id) => messageIdsToSync.add(id));
  });
  removedEventIds.forEach((eventId) => {
    messageIdsByEventIdRef.current.get(eventId)?.forEach((id) => messageIdsToSync.add(id));
  });
  messageIdsToSync.forEach((existingMessageId) => {
    const previousBlocks = blocksByMessageIdRef.current.get(existingMessageId) ?? [];
    const syncedBlocks = previousBlocks
      .filter((block) => !removedEventIds.has(block.eventId))
      .map((block) => updatedById.get(block.eventId) ?? block);
    blocksByMessageIdRef.current.set(existingMessageId, syncedBlocks);
    runtime.updateMessage(existingMessageId, { suggestedBlocks: syncedBlocks });
  });

  const messageId = nextResultMessageId(runtime, messageIdPrefix);
  blocksByMessageIdRef.current.set(messageId, blocks);
  runtime.addMessage({
    messageId,
    content: '',
    role: 'assistant',
    sentAt: new Date().toISOString(),
    suggestedBlocks: blocks,
  });
  blocks.forEach((block) => {
    const ids = messageIdsByEventIdRef.current.get(block.eventId) ?? new Set<string>();
    ids.add(messageId);
    messageIdsByEventIdRef.current.set(block.eventId, ids);
  });
};

const renderInlineScheduleBlocks = (
  runtime: PlanResultRuntime,
  resultJson: ChatResultJson,
  rawScheduleItems: ScheduleEvent[],
  resolvedTargetDate: string
): ChatSuggestedEvent[] => {
  if (resultJson.summary) {
    runtime.addMessage({
      messageId: `result_json_summary_${Date.now()}`,
      content: resultJson.summary,
      role: 'assistant',
      sentAt: new Date().toISOString(),
    });
  }
  const scheduleBlocks = transformResultJsonSchedulesToSuggestedEvents(
    rawScheduleItems,
    resolvedTargetDate
  );
  if (scheduleBlocks.length === 0) {
    return scheduleBlocks;
  }

  const updatedById = new Map(scheduleBlocks.map((block) => [block.eventId, block]));
  const messageIdsToSync = new Set<string>();
  scheduleBlocks.forEach((block) => {
    runtime.scheduleMessageIdsByActionIdRef.current.get(block.eventId)?.forEach((id) => {
      messageIdsToSync.add(id);
    });
  });
  messageIdsToSync.forEach((existingMessageId) => {
    const previousBlocks =
      runtime.scheduleBlocksByMessageIdRef.current.get(existingMessageId) ?? [];
    const syncedBlocks = previousBlocks.map((block) => updatedById.get(block.eventId) ?? block);
    runtime.scheduleBlocksByMessageIdRef.current.set(existingMessageId, syncedBlocks);
    runtime.updateMessage(existingMessageId, { suggestedBlocks: syncedBlocks });
  });

  const scheduleMessageId = nextResultMessageId(runtime, 'result_json_schedules');
  runtime.scheduleBlocksByMessageIdRef.current.set(scheduleMessageId, scheduleBlocks);
  runtime.addMessage({
    messageId: scheduleMessageId,
    content: '',
    role: 'assistant',
    sentAt: new Date().toISOString(),
    suggestedBlocks: scheduleBlocks,
  });
  scheduleBlocks.forEach((block) => {
    const ids =
      runtime.scheduleMessageIdsByActionIdRef.current.get(block.eventId) ?? new Set<string>();
    ids.add(scheduleMessageId);
    runtime.scheduleMessageIdsByActionIdRef.current.set(block.eventId, ids);
  });

  runtime.inlineScheduleRef.current = { messageId: scheduleMessageId, date: resolvedTargetDate };
  return scheduleBlocks;
};

const syncTemporalScheduleEvents = (
  runtime: PlanResultRuntime,
  rawScheduleItems: ScheduleEvent[],
  scheduleBlocks: ChatSuggestedEvent[]
): void => {
  const removedActionIds = new Set(
    rawScheduleItems
      .filter((item) => item.schedule_action === 'remove')
      .map((item) => item.action_id || item.event_id)
      .filter((id): id is string => !!id)
  );
  if (scheduleBlocks.length === 0 && removedActionIds.size === 0) {
    return;
  }
  runtime.setTemporalSuggestedEvents((prevEvents) => {
    const updatedById = new Map(scheduleBlocks.map((block) => [block.eventId, block]));
    const filteredPreviousEvents = prevEvents.filter(
      (event) => !updatedById.has(event.eventId) && !removedActionIds.has(event.eventId)
    );
    const upsertedBlocks = scheduleBlocks
      .filter((block) => !removedActionIds.has(block.eventId))
      .map((block) => ({ ...block, isTemporalPreview: true }));
    return [...filteredPreviousEvents, ...upsertedBlocks];
  });
};

/**
 * Drop temporal overlays for schedule action ids. Used by scheduler_chat /
 * plan-my-day where `suggestAllAIPlanState` is the single calendar source —
 * otherwise the same adjustment renders twice (raw action_id temporal +
 * `all_plan_${id}` replan preview).
 */
const clearTemporalScheduleEventsForItems = (
  runtime: PlanResultRuntime,
  rawScheduleItems: ScheduleEvent[]
): void => {
  const actionIds = new Set(
    rawScheduleItems
      .map((item) => item.action_id || item.event_id)
      .filter((id): id is string => !!id)
  );
  if (actionIds.size === 0) {
    return;
  }
  runtime.setTemporalSuggestedEvents((prevEvents) =>
    prevEvents.filter((event) => !actionIds.has(event.eventId))
  );
};

const renderInlineScheduleFromResultJson = (
  runtime: PlanResultRuntime,
  resultJson: ChatResultJson,
  rawScheduleItems: ScheduleEvent[],
  resolvedTargetDate: string,
  options?: { syncTemporal?: boolean }
): void => {
  if (rawScheduleItems.length === 0) {
    return;
  }
  const scheduleBlocks = renderInlineScheduleBlocks(
    runtime,
    resultJson,
    rawScheduleItems,
    resolvedTargetDate
  );
  if (options?.syncTemporal === false) {
    clearTemporalScheduleEventsForItems(runtime, rawScheduleItems);
  } else {
    syncTemporalScheduleEvents(runtime, rawScheduleItems, scheduleBlocks);
  }
  runtime.updateState({ activePlanContext: { targetDate: resolvedTargetDate } });
};

const mergeScheduleIntoAllPlanState = (
  runtime: PlanResultRuntime,
  rawScheduleItems: ScheduleEvent[],
  resolvedTargetDate: string
): void => {
  const transformedSchedules = transformAllPlanV7ScheduleEventsToScheduleAction(
    rawScheduleItems,
    resolvedTargetDate
  );
  if (transformedSchedules.length === 0) {
    return;
  }
  const removedIds = new Set(
    transformedSchedules
      .filter((schedule) => schedule.actionType === 'remove')
      .map((schedule) => schedule.id)
      .filter((id): id is string => !!id)
  );
  runtime.setSuggestAllAIPlanState((prev) => {
    const existingIds = new Set(prev.schedules.map((action) => action.id).filter(Boolean));
    const newSchedules = transformedSchedules.filter(
      (schedule) =>
        schedule.actionType !== 'remove' && (!schedule.id || !existingIds.has(schedule.id))
    );
    const updatedSchedules = prev.schedules
      .filter((existing) => !existing.id || !removedIds.has(existing.id))
      .map((existing) => {
        const updated = transformedSchedules.find(
          (schedule) =>
            schedule.id && schedule.id === existing.id && schedule.actionType !== 'remove'
        );
        if (!updated) {
          return existing;
        }
        return merge({}, existing, updated) as ScheduledAction;
      });
    const merged = [...updatedSchedules, ...newSchedules].filter(
      (action, idx, arr) => !action.id || arr.findIndex((item) => item.id === action.id) === idx
    );
    return { ...prev, schedules: merged, targetDate: resolvedTargetDate };
  });
};

const syncMealBlocks = (
  runtime: PlanResultRuntime,
  responses: ChatUnifiedResponseEntry[],
  resolvedTargetDate: string
) => {
  const mealBlocks = responses.flatMap((entry) => {
    const entryDate = entry?.date || resolvedTargetDate;
    return transformResultJsonMealsToSuggestedEvents(
      (entry?.adjustments?.meals ?? []) as unknown as Parameters<
        typeof transformResultJsonMealsToSuggestedEvents
      >[0],
      entryDate,
      runtime.getMealOnDate(dayjs(entryDate))?.details
    );
  });
  const removedMealEventIds = new Set(
    responses.flatMap((entry) =>
      getRemovedMealEventIds(
        (entry?.adjustments?.meals ?? []) as unknown as Parameters<
          typeof getRemovedMealEventIds
        >[0],
        entry?.date || resolvedTargetDate
      )
    )
  );
  syncSuggestionCard(
    runtime,
    mealBlocks,
    runtime.mealMessageIdsByEventIdRef,
    runtime.mealBlocksByMessageIdRef,
    'result_json_meals',
    removedMealEventIds
  );
};

const syncSleepBlock = (
  runtime: PlanResultRuntime,
  responses: ChatUnifiedResponseEntry[],
  resolvedTargetDate: string
) => {
  const rawSleepEntries = responses.map((entry) => entry?.adjustments?.sleep).filter(Boolean);
  const sleepBlock =
    rawSleepEntries.length > 0
      ? transformResultJsonSleepToSuggestedEvent(
          rawSleepEntries[rawSleepEntries.length - 1] as unknown as Parameters<
            typeof transformResultJsonSleepToSuggestedEvent
          >[0],
          resolvedTargetDate
        )
      : undefined;
  syncSuggestionCard(
    runtime,
    sleepBlock ? [sleepBlock] : [],
    runtime.sleepMessageIdsByEventIdRef,
    runtime.sleepBlocksByMessageIdRef,
    'result_json_sleep'
  );
};

const syncTaskBlocks = (
  runtime: PlanResultRuntime,
  responses: ChatUnifiedResponseEntry[],
  resolvedTargetDate: string
) => {
  const rawTasks = responses.flatMap(
    (entry) => (entry?.adjustments?.todo_list ?? []) as unknown as TodoAdjustment[]
  );
  const taskBlocks = transformResultJsonTasksToSuggestedEvents(
    rawTasks,
    resolvedTargetDate,
    runtime.findCurrentTasks()
  );
  syncSuggestionCard(
    runtime,
    taskBlocks,
    runtime.taskMessageIdsByEventIdRef,
    runtime.taskBlocksByMessageIdRef,
    'result_json_tasks',
    new Set(getRemovedTaskEventIds(rawTasks))
  );
};

const syncRoutineBlocks = (
  runtime: PlanResultRuntime,
  responses: ChatUnifiedResponseEntry[],
  resolvedTargetDate: string
) => {
  const rawRoutines = responses.flatMap(
    (entry) =>
      (entry?.adjustments?.routine_adjustments ?? []) as unknown as RoutineAdjustment[]
  );
  const routineBlocks = transformResultJsonRoutinesToSuggestedEvents(
    rawRoutines,
    resolvedTargetDate,
    runtime.findCurrentRoutineActivities()
  );
  syncSuggestionCard(
    runtime,
    routineBlocks,
    runtime.routineMessageIdsByEventIdRef,
    runtime.routineBlocksByMessageIdRef,
    'result_json_routines'
  );
};

const syncMealSleepTaskRoutineBlocks = (
  runtime: PlanResultRuntime,
  responses: ChatUnifiedResponseEntry[],
  resolvedTargetDate: string
): void => {
  syncMealBlocks(runtime, responses, resolvedTargetDate);
  syncSleepBlock(runtime, responses, resolvedTargetDate);
  syncTaskBlocks(runtime, responses, resolvedTargetDate);
  syncRoutineBlocks(runtime, responses, resolvedTargetDate);
};

const applyV4DirectAdjustments = (
  runtime: PlanResultRuntime,
  resultJson: ChatResultJson,
  sessionType: StreamChatSessionType,
  date: string | undefined
): void => {
  runtime.inlineScheduleRef.current = undefined;
  const rawScheduleItems = readScheduleItems(resultJson);
  const resolvedTargetDate = date || dayjs().format('YYYY-MM-DD');
  // scheduler_chat / plan-my-day merges into suggestAllAIPlanState for the
  // calendar grid — skip temporalSuggestedEvents so the same action_id is not
  // also painted as a purple chat overlay (would duplicate beside all_plan_*).
  const isSchedulerChat = sessionType === 'scheduler_chat';
  renderInlineScheduleFromResultJson(
    runtime,
    resultJson,
    rawScheduleItems,
    resolvedTargetDate,
    { syncTemporal: !isSchedulerChat }
  );
  if (isSchedulerChat && rawScheduleItems.length > 0) {
    mergeScheduleIntoAllPlanState(runtime, rawScheduleItems, resolvedTargetDate);
  }
  const responses = Array.isArray(resultJson.unified_chat_responses)
    ? resultJson.unified_chat_responses
    : [];
  syncMealSleepTaskRoutineBlocks(runtime, responses, resolvedTargetDate);
};

const renderInsightsAndQueryRichContext = (
  runtime: PlanResultRuntime,
  insights: InsightSuggestion[],
  queryEntries: UnifiedChatRichContextEntry[]
): void => {
  if (insights.length === 0 && queryEntries.length === 0) {
    return;
  }
  const streamingMessageId = runtime.streamingDataRef.current.streamingMessageId;
  if (streamingMessageId) {
    runtime.updateMessage(streamingMessageId, {
      insightSuggestions: insights.length > 0 ? insights : undefined,
      richContexts: queryEntries.length > 0 ? queryEntries : undefined,
    });
    return;
  }
  runtime.addMessage({
    messageId: `suggestions_${Date.now()}`,
    content: '',
    role: 'assistant',
    sentAt: new Date().toISOString(),
    insightSuggestions: insights.length > 0 ? insights : undefined,
    richContexts: queryEntries.length > 0 ? queryEntries : undefined,
  });
};

const isHandledDirectlyFromResultJson = (
  runtime: PlanResultRuntime,
  resultJson: ChatResultJson,
  sessionType: StreamChatSessionType
): boolean => {
  const hasUnifiedChatResponses =
    Array.isArray(resultJson.unified_chat_responses) &&
    resultJson.unified_chat_responses.length > 0;
  return (
    runtime.isV4Enabled &&
    (sessionType === 'general' ||
      sessionType === 'scheduler_chat' ||
      sessionType === 'recommendation' ||
      (sessionType === 'task' && hasUnifiedChatResponses))
  );
};

const callLegacySyncChatResultIfNeeded = async (
  runtime: PlanResultRuntime,
  originalResult: unknown,
  resultJson: ChatResultJson,
  sessionType: StreamChatSessionType,
  date: string | undefined,
  currentSessionId: string | undefined,
  adjustmentEntries: UnifiedChatRichContextEntry[]
): Promise<void> => {
  if (!runtime.user || isHandledDirectlyFromResultJson(runtime, resultJson, sessionType)) {
    return;
  }

  if (!currentSessionId) {
    console.warn('Skipping legacy chat result sync: sessionId is missing');
    return;
  }

  const formattedResult = await runtime.syncChatResult({
    sessionId: currentSessionId,
    sessionType,
    resultJson:
      typeof originalResult === 'string' ? originalResult : JSON.stringify(originalResult),
    userId: runtime.user.userId,
    metricId: runtime.user.metricId!,
    calendarId: runtime.calendarId,
    dateToBeSuggested: date,
    routineId: runtime.user.routineId,
  });

  runtime.handleFormattedResult(formattedResult, sessionType, date, adjustmentEntries);
};

export interface ProcessFinalChatResultArgs {
  resultJson: ChatResultJson;
  originalResult: unknown;
  sessionIdFromPayload?: string;
  sessionType: StreamChatSessionType;
  date?: string;
  sessionId?: string;
  prompt?: string;
  stateSessionId: string | null;
  runtime: PlanResultRuntime;
}

export const processFinalChatResult = async ({
  resultJson,
  originalResult,
  sessionIdFromPayload,
  sessionType,
  date,
  sessionId,
  prompt,
  stateSessionId,
  runtime,
}: ProcessFinalChatResultArgs): Promise<void> => {
  const suggestions = normalizeInsightSuggestions(resultJson.suggestions);
  const richContextEntries = extractRichContextEntries(resultJson);
  runtime.latestResultJsonRef.current = resultJson;
  runtime.latestSuggestionsRef.current = suggestions;

  if (runtime.isV4Enabled) {
    applyV4DirectAdjustments(runtime, resultJson, sessionType, date);
  }

  const queryEntries = richContextEntries.filter((entry) => entry.source === 'query');
  const adjustmentEntries = richContextEntries.filter((entry) => entry.source === 'adjustment');
  renderInsightsAndQueryRichContext(runtime, suggestions.slice(0, 6), queryEntries);

  if (!runtime.user || !originalResult) {
    return;
  }

  runtime.setFeedbackData((prev) => ({
    ...(prev ?? { currentTime: new Date().toISOString() }),
    appApiRes: JSON.stringify(originalResult),
  }));

  await callLegacySyncChatResultIfNeeded(
    runtime,
    originalResult,
    resultJson,
    sessionType,
    date,
    sessionIdFromPayload || sessionId || stateSessionId || undefined,
    adjustmentEntries
  );

  await runtime.discardPendingAllPlanSuggestions(
    getAllPlanTypesToDiscardForChatOutput(sessionType, prompt),
    date
  );
};
